import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { castArray } from 'lodash'
import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns'
import {
  sanitizeForSearch,
  parseDateFromString,
  convertToSearchParams,
  useSearchParams,
} from 'utils'
import { AccountAudience } from 'config/portal'

export const INITIAL_FILTERS = {
  agreementStatus: [],
  initialInvestment: [],
  planSponsor: [],
  startLastUpdatedDate: '',
  endLastUpdatedDate: '',
}

export function useServicesAgreementsFilters({
  servicesAgreements,
  currentUser,
}) {
  const history = useHistory()
  const location = useLocation()
  const searchParams = useSearchParams()

  const filters = useMemo(() => {
    let initialFilters = { ...INITIAL_FILTERS }
    const {
      agreementStatus,
      initialInvestment,
      planSponsor,
      startLastUpdatedDate,
      endLastUpdatedDate,
    } = searchParams

    if (agreementStatus)
      initialFilters.agreementStatus = castArray(agreementStatus)
    if (initialInvestment)
      initialFilters.initialInvestment = castArray(initialInvestment)
    if (planSponsor) initialFilters.planSponsor = castArray(planSponsor)
    if (startLastUpdatedDate)
      initialFilters.startLastUpdatedDate = startOfDay(
        parseDateFromString(startLastUpdatedDate)
      )
    if (endLastUpdatedDate)
      initialFilters.endLastUpdatedDate = startOfDay(
        parseDateFromString(endLastUpdatedDate)
      )

    return initialFilters
  }, [searchParams])

  const searchValue = searchParams.q ?? ''

  const updateSearchParams = (filters, searchValue) => {
    history.push({
      pathname: location.pathname,
      search: convertToSearchParams({ ...filters, q: searchValue }),
    })
  }

  const filteredServicesAgreements = useMemo(() => {
    if (!servicesAgreements) return

    const filtered = servicesAgreements.filter(
      ({
        portalAgreementStatus,
        portalAgreementType,
        planSponsorCompany,
        createdDate,
      }) => {
        const {
          agreementStatus,
          initialInvestment,
          planSponsor,
          startLastUpdatedDate,
          endLastUpdatedDate,
        } = filters
        const lastUpdatedDate = parseDateFromString(createdDate)
        return (
          (agreementStatus.length
            ? agreementStatus.includes(portalAgreementStatus)
            : true) &&
          (initialInvestment.length
            ? initialInvestment.includes(portalAgreementType)
            : true) &&
          (planSponsor.length
            ? planSponsor.includes(planSponsorCompany.name)
            : true) &&
          (startLastUpdatedDate
            ? isAfter(lastUpdatedDate, startLastUpdatedDate) ||
              isSameDay(lastUpdatedDate, startLastUpdatedDate)
            : true) &&
          (endLastUpdatedDate
            ? isBefore(lastUpdatedDate, endLastUpdatedDate) ||
              isSameDay(lastUpdatedDate, endLastUpdatedDate)
            : true)
        )
      }
    )

    const sanitizedValue = sanitizeForSearch(searchValue)
    if (sanitizedValue === '') return filtered
    if (currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY) {
      return filtered.filter(({ name }) =>
        sanitizeForSearch(name).includes(sanitizedValue)
      )
    }
    return filtered.filter(
      ({ name, planSponsorCompany, planEIN }) =>
        sanitizeForSearch(name).includes(sanitizedValue) ||
        (planSponsorCompany.name &&
          sanitizeForSearch(planSponsorCompany.name).includes(
            sanitizedValue
          )) ||
        (planEIN && sanitizeForSearch(planEIN).includes(sanitizedValue))
    )
  }, [filters, searchValue, servicesAgreements])

  return {
    filters,
    searchValue,
    updateSearchParams,
    filteredServicesAgreements,
  }
}
